interface SuccessObject {
  key: string;
  text: string;
  value: string;
  checked?: boolean;
}

const successMessageMap: Record<string, Record<string, string>> = {
  sendemail: {
    registration: "A verification email has been sent successfully on your registered Email ID.",
    forgotpassword: "A password reset email has been sent with further instructions.",
    oneclicksignin: "A verification email for passwordless login has been sent successfully on your registered Email ID.",
    deleteaccount: "A confirmation email for deleting your account has been sent with further instructions.",
    forgotpin: "A PIN reset email has been sent with further instructions.",
    add_email: "A verification email has been sent to add an additional email.",
    secondfactorauthentication: "An OTP has been sent successfully via email"
  },
  sendsuspendedemail: {
    registration: "A verification email has been sent successfully on your registered Email ID.",
    forgotpassword: "A password reset email has been sent with further instructions.",
    oneclicksignin: "A verification email for passwordless login has been sent successfully on your registered Email ID.",
    deleteaccount: "A confirmation email for deleting your account has been sent with further instructions.",
    forgotpin: "A PIN reset email has been sent with further instructions.",
    add_email: "A verification email has been sent to add an additional email.",
    secondfactorauthentication: "An OTP has been sent successfully via email"
  },
  sendsms: {
    verification: "A Verification OTP has been sent successfully on your registered mobile number.",
    onetimepasscode: "An OTP has been sent successfully via SMS.",
    secondfactorauthentication: "An OTP has been sent successfully via SMS.",
    changephoneno: "A verification OTP has been sent to your new mobile number.",
    forgotpin: "A PIN reset OTP has been sent successfully via SMS.",
    forgotpassword: "A password reset OTP has been sent successfully via SMS."
  },
  verifyemail: {
    registration: "Your email has been verified successfully. You're all set to proceed",
    add_email: "Your email has been verified successfully.",
    oneclicksignin: "Your email has been verified successfully. You're all set to proceed",
    deleteaccount: "Your account has been deleted successfully."
  },
  createuser: {
    default: "User created successfully."
  },
  deleteuser: {
    default: "User deleted successfully."
  },
  verifyotp: {
    default: "OTP verified successfully."
  },
  consentstatus: {
    default: "Consent status updated successfully."
  },
  accountactive: {
    default: "Account activated successfully."
  },
  unlockaccount: {
    default: "Account unlocked successfully."
  },
  mfaupdatephone: {
    default: "MFA phone number updated successfully."
  },
  verifyauthenticatortotp: {
    default: "Authenticator app verified successfully."
  },
  verifysecurityquestion: {
    default: "Security question verified successfully."
  },
  mfaresetauthenticator: {
    default: "MFA authenticator reset successfully."
  },
  mfaresetbackupcode: {
    default: "MFA backup codes reset successfully."
  },
  mfadownloadbackupcode: {
    default: "MFA backup codes downloaded successfully."
  },
  mfaupdatesecurityanswer: {
    default: "MFA security answer updated successfully."
  },
  verifybackupcode: {
    default: "Backup code verified successfully."
  },
  verifypin: {
    default: "PIN verified successfully."
  },
  updatepin: {
    default: "PIN updated successfully."
  },
  updateuser: {
    default: "UserInfo updated successfully."
  },
  assignrole: {
    default: "Role assigned successfully."
  },
  logout: {
    default: "Logged out successfully."
  },
  userexists: {
    default: "User already exists."
  }
};

export const SUCCESS_HANDLING_LABEL = "Success Handling";
export const SUCCESS_HANDLING_PROPERTY_ID = "successHandling";

export const GetSuccessDefaultsForNode = (nodeType: string): Record<string, string> => {
  return successMessageMap[nodeType] ? { ...successMessageMap[nodeType] } : {};
};

export const GetSuccessDefaultText = (nodeType: string, key: string): string => {
  if (!nodeType || !key) {
    return "";
  }
  return successMessageMap[nodeType]?.[key] || "";
};

export const GetSuccessArrayForNodeType = (nodeType: string): SuccessObject[] => {
  const defaults = successMessageMap[nodeType];
  if (!defaults) {
    return [];
  }
  return Object.entries(defaults).map(([key, text]) => ({
    key,
    text,
    value: "automatic",
    checked: false
  }));
};

const mergeSuccessValues = (current: SuccessObject[], defaults: SuccessObject[]): SuccessObject[] => {
  const safeCurrent = Array.isArray(current) ? current.filter((item) => item && typeof item.key === "string").map((item) => ({ ...item, checked: item.checked ?? false })) : [];
  const currentMap = new Map(safeCurrent.map((item) => [item.key, item]));

  const merged = defaults.map((def) => {
    if (currentMap.has(def.key)) {
      const existing = currentMap.get(def.key) as SuccessObject;
      return {
        key: def.key,
        text: existing.text ?? def.text,
        value: existing.value ?? def.value,
        checked: existing.checked ?? false
      };
    }
    return { ...def };
  });

  // Preserve any custom success entries that may have been added manually
  safeCurrent.forEach((item) => {
    if (!defaults.find((def) => def.key === item.key)) {
      merged.push(item);
    }
  });

  return merged;
};

export const applySuccessHandlingToNode = (node: any) => {
  if (!node || !node.type || !node.data) {
    return node;
  }
  const defaults = GetSuccessArrayForNodeType(node.type);
  if (!defaults.length) {
    return node;
  }

  const properties = Array.isArray(node.data.properties) ? [...node.data.properties] : [];
  const propertyIndex = properties.findIndex((prop: any) => prop.elementType === "successarray");

  if (propertyIndex === -1) {
    return {
      ...node,
      data: {
        ...node.data,
        properties: [
          ...properties,
          {
            id: SUCCESS_HANDLING_PROPERTY_ID,
            type: "custommultiselect",
            value: defaults,
            label: SUCCESS_HANDLING_LABEL,
            elementType: "successarray"
          }
        ]
      }
    };
  }

  const existingProperty = properties[propertyIndex];
  const mergedValues = mergeSuccessValues(existingProperty?.value || [], defaults);
  const updatedProperties = [...properties];
  updatedProperties[propertyIndex] = {
    ...existingProperty,
    type: existingProperty.type || "custommultiselect",
    label: existingProperty.label || SUCCESS_HANDLING_LABEL,
    elementType: "successarray",
    value: mergedValues
  };

  return {
    ...node,
    data: {
      ...node.data,
      properties: updatedProperties
    }
  };
};

export default successMessageMap;
